import { motion } from 'framer-motion';
import { Flame, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProgress } from '@/hooks/useProgress';
import type { TabId } from '@/components/BottomNav';

interface WeeklyRecapCardProps {
  onNavigate?: (tab: TabId) => void;
}

/**
 * Condensed weekly recap — a single slim row showing current streak and
 * total XP plus one CTA. Leaderboard and Gems live in the Profile tab now,
 * so they intentionally do not appear here.
 */
export function WeeklyRecapCard({ onNavigate }: WeeklyRecapCardProps = {}) {
  const { progress } = useProgress();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="glass-card shadow-elevated px-4 py-3 flex items-center gap-4"
    >
      {/* Streak */}
      <div className="flex items-center gap-1.5 min-w-0">
        <Flame className="w-4 h-4 text-warning drop-shadow-[0_0_6px_hsl(var(--warning)/0.7)] shrink-0" />
        <span className="display-number text-sm text-warning">{progress.streak}</span>
        <span className="text-[11px] font-body text-muted-foreground">day</span>
      </div>

      <span className="h-4 w-px bg-white/10" aria-hidden="true" />

      {/* Total XP */}
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <Zap className="w-4 h-4 text-primary shrink-0" />
        <span className="display-number text-sm text-primary truncate">
          {progress.xp.toLocaleString()}
        </span>
        <span className="text-[11px] font-body text-muted-foreground">XP</span>
      </div>

      {/* Single CTA */}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onNavigate?.('profile')}
        className="h-8 px-3 text-xs font-display font-bold text-foreground/80 hover:text-foreground"
      >
        View progress
        <ArrowRight className="ml-1 w-3.5 h-3.5" />
      </Button>
    </motion.div>
  );
}
